import React, { createRef, useRef, useState, createContext, useEffect } from 'react';
import { v4 as getUUID } from 'uuid';
import _omit from 'lodash/omit';
import _merge from 'lodash/merge';
import _isEmpty from 'lodash/isEmpty';
import _flatten from 'lodash/flatten';
import { SPACING_4 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import FindIcon from '@nokia-csf-uxr/ccfk-assets/FindIcon';
import { getContext, useCombinedRefs } from 'common';
import useHover from 'common/useHover';
import KEY_CODES from 'common/KeyCodes';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import List, { ListItemBasic, ListItemText } from 'components/List';
import Menu from 'components/Menu';
import Chip, { ChipLabel } from 'components/Chip';
import Icon, { IconProps } from 'components/Icon';
import { SearchwChipsCore } from 'components/SearchwChips';
import SearchwChipsTextInput from './SearchwChipsTextInput';
import ChipIconButton from '../Chip/ChipIconButton';
import SearchwChipsOperator from './SearchwChipsOperator';

const {
  ENTER_KEY,
  DELETE,
  BACKSPACE,
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
  SPACE_KEY,
  ARROW_UP
} = KEY_CODES;

const ARIA_LABEL_FIELD_BEFORE = 'Press backspace to remove ';
const ARIA_LABEL_FIELD_AFTER = ' Press delete to remove ';
interface SearchwChipsContextInterface {
  size?: 'small' | 'medium';
}

const SearchwChipsContext = createContext<SearchwChipsContextInterface | null>({});
const CHIP_MENU_CONFIG = {
  usePortal: true,
  positioningOptions: {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 0]
        }
      }
    ]
  }
};

const getIndexById:(data: any, id: any) => number = (data, id) => data.findIndex(item => item.id === id);
const SearchwChipsIcon = props => getContext(({ theme }) => <Icon {...props} color={theme.SEARCH_W_CHIPS.icon}><FindIcon /></Icon>);
const resolveSubFiled = data => data.map((dataItem, index: number) => {
  const canBePair = dataItem.type === 'sub-field' && dataItem.parentId !== undefined;
  const nextItemIsPair = canBePair && (data[index + 1] && data[index + 1].type === 'sub-field' && data[index + 1].parentId === dataItem.parentId);
  const preItemIsPair = canBePair && (data[index - 1] && data[index - 1].type === 'sub-field' && data[index - 1].parentId === dataItem.parentId);
  const subField = nextItemIsPair || preItemIsPair;
  return (subField ? {
    ...dataItem,
    subField: true
  } : _omit(dataItem, 'subField'));
});

const omitSubFieldProp = data => data.map(dataItem => _omit(dataItem, 'subField'));

const EMPTY_STRING = '';

const CHIP_CONFIG = {
  style: {
    margin: `${SPACING_4} 0`
  },
  role: 'button',
  tabIndex: -1
};

export interface SearchwChipsProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /**
     * The data of the component (An array of chips, fields or sub-fields). Each data item has the following properties:
     *
     *      `id`: The unique id of the item.
     *
     *      `type`: The type of the item.
     *          "chip" to render Chip.
     *          "field" the field for query only SearchwChips.
     *          "sub-field" should be used to make scope, condition, and query fields.
     *          "operator" is a special type of chip that renders operators for creating expression builder.
     *
     *      `renderNode`: To override the item. The custom component should support `Chip` and `SearchwChipsTextInput` API.
     *
     *      `focused`: Set `true` if the item (chip, field, or sub-field) is focused.
     *
     *      `listFocused`: Set `true` if the focus is on the autocomplete list
     *
     *      `popupFocused`: Set `true` if the focus is on the popup, such as calendar.
     *
     *      `selected`: Set `true` if the chip is selected.
     *
     *      `error`: Set `true` if the chip is in an error state.
     *
     *      `value`: The value of the item. The `value` of the chip is a string for query only SearchwChips or it can be a string also for fields and sub-fields. But chips `value` should be an array to make scope, condition, and queries.
     *       In case of making scope, condition, and query, each item of the value array should include the following required properties:
     *          `id`: The unique id of the value item.
     *          `value`: The value of the value item.
     *          `separator`: A string that can be used for joining values to make the chip label.
     *          `parentId`: It is required and for each chip, it should be the same and equal to the chip's id.
     *
     *      `parentId`: It is required for sub-fields to group them. (Sub-fields with the same `parentId` will make a chip together.)
     *
     *      `contextMenu`: ContextMenu configuration used by chip, field, and sub-field. (This can be used for copy and paste functionalities)
     *          `contextMenu.event`: The event toggling the contextmenu. It is used to find the position of the pointer.
     *          `contextMenu.items`: List items configuration, each item of this array include the following properties:
     *              `key`: The unique key of the contextual menu item.
     *              `content`: The content of the contextual menu item.
     *              `onClick`: The callback that is triggered by clicking on the item.
     *
     *       `autocomplete`: Autocomplete (autosuggest) configurations used by field and sub-fields.
     *           `autocomplete.event`: The event toggling the list, it is used to find the position of the pointer. Omit this to render the list under the field.
     *           `autocomplete.items`: autocomplete items array, each item include the following properties:
     *              `itemNode`: To override the list item
     *              `key`: The unique key of the list item. (it is required if `itemNode` is not provided)
     *              `content`: The content of the list item. (it is required if `itemNode` is not provided)
     *              `searchPattern`: The inserted value in the corresponding field. It is used for highlighting the searched string when content is provided as a string.
     *              `onClick`: The callback that is triggered by clicking on the item.
     *              `submitChip`: Set to `true` to convert the grouped sub-fields to chip after choosing this item from the list.
     *
     *        `listProps`: To configure the attached list, refer to the <SearchwChipsList> documentation for more information.
     *        `renderPopup`: To render any node attached to the field or sub-field.
     *                       This can be used for attaching a Calendar or a Range component.
     *                       The popup node should be wrapped by a component that utilizes `usePositionPopup` and should pass all properties of `positioningProps` prop to `usePositionPopup`, please refer to `usePositionPopup`'s API for more information.
     *                       The popup should support all the following props
     *                          `focusOnField`: When it is called SearchWChips focuses on the belonging field of this popup.
     *                          `onMouseDown`: onMouseDown should be passed to the outermost element
     *                          `onBlur`: onBlur should be passed to the outermost element
     *                          `onSubmit`: When it is called SearchWChips converts all draft fields to a chip.
     *                          `positioningProps`: `usePositionPopup` configuration.
     */
  data: {
    id: string,
    type?: 'field' | 'chip' | 'sub-field' | 'operator',
    renderNode?(any):JSX.Element,
    focused?: boolean,
    listFocused?: boolean,
    popupFocused?: boolean,
    selected?: boolean,
    error?: boolean,
    value?: string | {
      id: string,
      value: string,
      separator: React.ReactNode
    }[],
    parentId?: string,
    contextMenu?: {
      event?: {},
      items: {
        key: string,
        content?: React.ReactNode,
        onClick?: React.MouseEventHandler
      }[]
    },
    autocomplete?: {
      event?: {},
      items: {
        itemNode?: React.ReactNode,
        key?: string,
        content?: React.ReactNode,
        searchPattern?: string,
        onClick?: React.MouseEventHandler
        submitChip?: boolean
      }[]
    },
    listProps?: {},
    renderPopup?(any): JSX.Element
  }[];
  /**
   * Callback fired with updated data when it is changed.
   * Signature:
   * function(data) => void
   * (SearchWChip returns the updated the focus state and the value of the items.)
   */
  onChange?: (arg: any[]) => void;
  /** The size of the component. */
  size?: 'small' | 'medium';
  /** The FilterField styling variant. */
  variant?: 'outlined' | 'underlined';
  /** If `true` the last field gets focus after initial render. */
  autoFocus?: boolean;
  /** Set to `true` to render in error state. */
  error?: boolean;
  /** The icon of component that is rendered before the first field. */
  SIcon?: (arg: Partial<IconProps>) => JSX.Element;
  /** Any element such as <SearchwChipsGlobalLogic /> that is rendered before chips (on the left). */
  beforeChips?: React.ReactNode;
  /** Any element such as <IconButton /> that is rendered after chips (on the right). */
  afterChips?: React.ReactNode;
  /** To override the vertical divider. */
  verticalDivider?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** Any other props than the `SearchwChips` props will be provided to the root element. */
const SearchwChips = React.forwardRef<HTMLElement, SearchwChipsProps>((props: SearchwChipsProps, forwardedRef) => getContext(({ theme }) => {
  const {
    data: dataProp,
    onChange: onChangeProp,
    size,
    variant,
    theme: themeProps,
    autoFocus,
    onFocus,
    onBlur,
    ...otherProps
  } = props;

  const [popupFocusID, setPopupFocusID] = useState(null);

  const forceFocusOnAttachment = useRef(null);
  // Cleanup before calling onChange
  const onChange = (newData) => {
    onChangeProp(omitSubFieldProp(newData));
  };

  // To render with sub-field visual there should be at least two adjacent sub-fields.
  const data = resolveSubFiled(dataProp);
  const resolvedTheme = _merge({}, theme.SEARCH_W_CHIPS, themeProps);

  const [hoverRef, hovered] = useHover();
  const ref = useCombinedRefs(forwardedRef, hoverRef);

  const refs = useRef(data.map(() => createRef()));
  const searchWChipsListRef = useRef(null);
  const searchWChipsPopupRef = useRef(null);

  const clickHandle = useRef<any | null>(null);

  const handlePopupBlur = () => {
    setPopupFocusID(null);
  };

  const handlePopupFocus = (targetItemID) => {
    setPopupFocusID(targetItemID);
  };

  useDidUpdateEffect(() => {
    onChange(data.map(item => ({ ...item, popupFocused: popupFocusID === item.id })));
  }, [popupFocusID]);

  // Handle autofocus
  useEffect(() => {
    const isAnItemFocused = data.some(item => item.focused || item.listFocused || item.popupFocused);
    if (!isAnItemFocused && autoFocus) {
      refs.current
      && refs.current[data.length - 1]
      && refs.current[data.length - 1].current
      && refs.current[data.length - 1].current.focus();
    }
  }, []);

  /* Focus on an item (chip or sub-field/field) */
  useEffect(() => {
    const focusIndex = data.findIndex(item => item.focused);
    if (focusIndex > -1) {
      refs.current[focusIndex]
      && refs.current[focusIndex].current
      && refs.current[focusIndex].current.focus();
    }
  }, [
    data.findIndex(item => item.focused),
    data.length,
    data.filter(item => item.type === 'sub-field').length,
    data.filter(item => item.type === 'field').length,
    data.filter(item => item.type === 'chip').length
  ]);

  /* Force focus on the list. */
  useEffect(() => {
    if (forceFocusOnAttachment.current) {
      forceFocusOnAttachment.current = false;
      data.findIndex(item => item.listFocused) !== -1
        && searchWChipsListRef
        && searchWChipsListRef.current
        && searchWChipsListRef.current.focus();
    }
  }, [data.findIndex(item => item.listFocused)]);

  /* Force focus on the popup. */
  useEffect(() => {
    if (forceFocusOnAttachment.current) {
      forceFocusOnAttachment.current = false;
      data.findIndex(item => item.popupFocused) !== -1
        && searchWChipsPopupRef
        && searchWChipsPopupRef.current
        && searchWChipsPopupRef.current.focus();
    }
  }, [data.findIndex(item => item.popupFocused)]);

  /* Update data with focused on list or popup */
  const focusOnAttachment = (id) => {
    const targetItemIndex:number = getIndexById(data, id);
    if (searchWChipsListRef
      && searchWChipsListRef.current
      && data[targetItemIndex].autocomplete
      && data[targetItemIndex].autocomplete.items.some(autocompleteItem => !autocompleteItem.disabled)
    ) {
      forceFocusOnAttachment.current = true;
      onChange(data.map(item => ({ ...item, focused: false, popupFocused: false, listFocused: item.id === id })));
    }
    if (data[targetItemIndex].renderPopup) {
      forceFocusOnAttachment.current = true;
      onChange(data.map(item => ({ ...item, focused: false, listFocused: false, popupFocused: item.id === id })));
    }
  };

  /** Remove a chip and its next field/subfield(s), update the focus index. */
  const removeChip = (chipId, source = data) => {
    let deleteCount = 1;
    const targetIndex:number = getIndexById(source, chipId);
    if (source[targetIndex + 1].type === 'sub-field') {
      const nextSubFieldParentId = source[targetIndex + 1].parentId;
      const removalSubFields = source.filter((item, index) => index >= targetIndex + 1 && nextSubFieldParentId === item.parentId);
      deleteCount += removalSubFields.length;
    } else {
      deleteCount += 1;
    }
    const focusedIndexAfterRemoval = targetIndex - 1;
    const resultData = source.map((item, index) => ({
      ...item,
      focused: index === focusedIndexAfterRemoval
    }));
    resultData.splice(targetIndex, deleteCount);
    return { resultData, deleteCount };
  };

  /** Remove a single field, update the focus index. */
  const removeField = (fieldIndex) => {
    const focusedIndexAfterRemoval = fieldIndex - 1;
    const focusedUpdatedData = data.map((item, index) => ({
      ...item,
      focused: index === focusedIndexAfterRemoval
    }));
    focusedUpdatedData.splice(fieldIndex, 1);
    return focusedUpdatedData;
  };

  const removeSelectedAndFocusedChips = () => {
    let removedCount = 0;
    let result = [...data];
    const removalChipsIndexes = [];
    data.forEach((item, index) => {
      if (item.focused || item.selected) {
        removalChipsIndexes.push(index);
      }
    });
    removalChipsIndexes.forEach((removalChipIndex) => {
      const {
        resultData,
        deleteCount
      } = removeChip(result[removalChipIndex - removedCount].id, result);
      result = [...resultData];
      removedCount += deleteCount;
    });
    onChange(result);
  };

  const convertFieldToChip = ({ id, index = undefined, value = undefined }: any) => {
    const targetIndex:number = index === undefined ? getIndexById(data, id) : index;
    const isNextField = data[targetIndex + 1] && data[targetIndex + 1].type === 'field';
    const isPreField = data[targetIndex - 1] && data[targetIndex - 1].type === 'field';
    const result = data.map((dataItem, itemIndex) => dataItem.id === id ? {
      ..._omit(dataItem, 'listFocused', 'popupFocused'),
      value: value === undefined ? dataItem.value : value,
      type: 'chip',
      selected: false,
      focused: false
    } : {
      ..._omit(dataItem, 'listFocused', 'popupFocused'),
      focused: isNextField && itemIndex === targetIndex + 1
    });
    if (!isNextField) {
      result.splice(targetIndex + 1, 0, {
        focused: true, // a new chip is added
        type: 'field',
        id: getUUID(),
        value: EMPTY_STRING
      });
    }
    if (!isPreField) {
      result.splice(targetIndex, 0, {
        focused: false,
        type: 'field',
        id: getUUID(),
        value: EMPTY_STRING
      });
    }
    return result;
  };

  const convertSubFieldsToChip = ({ id, index, value = undefined }) => {
    const targetIndex = index === undefined ? getIndexById(data, id) : index;
    const parentIdOfMatchedSubFields = data[targetIndex].parentId;
    const allMatchedSubFields = data.filter(item => item.parentId === parentIdOfMatchedSubFields);
    const firstSubFieldIndex:number = getIndexById(data, allMatchedSubFields[0].id);
    const lastSubFieldIndex:number = getIndexById(data, allMatchedSubFields[allMatchedSubFields.length - 1].id);
    const newChipId = parentIdOfMatchedSubFields;
    const resultChip = {
      type: 'chip',
      id: newChipId,
      value: [],
    };
    allMatchedSubFields.forEach((item) => {
      if (item.id === id && value !== undefined) {
        resultChip.value.push({ ...item, value, listFocused: false, popupFocused: false, focused: false });
        return;
      }
      if (item.value !== EMPTY_STRING && item.value !== undefined) {
        resultChip.value.push({ ...item, listFocused: false, popupFocused: false, focused: false });
      }
    });
    const isNextField = data[lastSubFieldIndex + 1] && data[lastSubFieldIndex + 1].type === 'sub-field';
    const isPreField = data[firstSubFieldIndex - 1] && data[firstSubFieldIndex - 1].type === 'sub-field';
    if (_isEmpty(resultChip.value)) {
      return data;
    }
    const result = [...data];
    result.splice(firstSubFieldIndex, allMatchedSubFields.length, resultChip);
    if (!isNextField) {
      result.splice(firstSubFieldIndex + 1, 0, {
        type: 'sub-field',
        id: getUUID(),
        parentId: getUUID(),
        value: EMPTY_STRING
      });
    }
    if (!isPreField) {
      result.splice(firstSubFieldIndex, 0, {
        focused: false,
        type: 'sub-field',
        id: getUUID(),
        parentId: getUUID(),
        value: EMPTY_STRING
      });
    }
    const focusIndex = firstSubFieldIndex + 1 + (isPreField ? 0 : 1);
    return result.map((item, itemIndex) => ({ ...item, focused: itemIndex === focusIndex }));
  };

  const submitAutoSuggestItem = ({ submitChip, subField, ...updatedItem }) => {
    onChange(convertFieldToChip(updatedItem));
  };

  const handleSubFieldAutocompleteItemClick = ({
    id: updatedItemId,
    key,
    label,
    content,
    index,
    submitChip,
    ...updatedItem
  }) => {
    if (submitChip === undefined || submitChip === false) {
      let nextFocusIndex;
      onChange(data.map((dataItem, dataIndex:number) => {
        if (dataItem.id === updatedItemId) {
          nextFocusIndex = (nextFocusIndex === undefined
            && data[dataIndex + 1]
            && data[dataIndex + 1].parentId === data[dataIndex].parentId
          ) ? dataIndex + 1 : dataIndex;
          return ({
            ...dataItem,
            ...updatedItem,
            focused: nextFocusIndex === dataIndex,
            listFocused: false
          });
        }
        return ({
          ...dataItem,
          focused: nextFocusIndex === dataIndex,
          listFocused: false
        });
      }));
      return;
    }
    onChange(convertSubFieldsToChip({ ...updatedItem, index, id: updatedItemId }));
  };

  const convertChipToField = (id) => {
    const result = data.map((dataItem) => {
      if (dataItem.id === id) {
        if (typeof dataItem.value === 'string') {
          return ({
            ...dataItem,
            type: 'field',
            focused: true
          });
        }
        return dataItem.value.map((valueItem, index) => ({
          ...valueItem,
          type: 'sub-field',
          focused: index === dataItem.value.length - 1
        }));
      }
      return _omit(dataItem, 'focused', 'listFocused', 'popupFocused');
    });
    return _flatten(result);
  };

  const focusOnIndex = (targetItemIndex, noListFocused = undefined, noPopupFocused = undefined) => {
    onChange(data.map((item, index) => ({
      ...item,
      focused: index === targetItemIndex,
      listFocused: noListFocused ? false : item.listFocused,
      popupFocused: noPopupFocused ? false : item.popupFocused
    })));
  };

  const handleFieldKeyDown = (eventData, { id }) => {
    // Focus on pre item
    if (eventData.key === ARROW_LEFT && eventData.target.selectionStart === 0) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT && (eventData.target.selectionStart > eventData.target.value.length - 1)) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    // Focus on the autocomplete list or popup
    if (eventData.key === ARROW_DOWN) {
      focusOnAttachment(id);
    }

    // Submit the chip
    if (eventData.key === ENTER_KEY && eventData.target.value !== EMPTY_STRING) {
      const newData = convertFieldToChip({ id });
      onChange(newData);
      return;
    }

    // Remove pre item
    if (eventData.key === BACKSPACE && eventData.nativeEvent.target.value === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id);
      if (targetIndex > 0) {
        if (data[targetIndex - 1].type === 'field') {
          onChange(removeField(targetIndex));
        } else {
          onChange(removeChip(data[targetIndex - 1].id).resultData);
        }
      }
    }

    // Remove next chip
    if (eventData.key === DELETE && eventData.nativeEvent.target.value === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id) + 1;
      if (targetIndex < data.length) {
        onChange(removeChip(data[targetIndex].id).resultData);
      }
    }
  };

  const handleSubFieldKeyDown = (eventData, { id, index }) => {
    // Focus on pre item
    if (eventData.key === ARROW_LEFT && eventData.target.selectionStart === 0) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT && (eventData.target.selectionStart > eventData.target.value.length - 1)) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    // Focus on the autocomplete list
    if (eventData.key === ARROW_DOWN) {
      focusOnAttachment(id);
    }

    // Submit the chip
    if (eventData.key === ENTER_KEY) {
      const newData = convertSubFieldsToChip({ id, index });
      onChange(newData);
      return;
    }

    // Remove pre field/chip
    if (eventData.key === BACKSPACE && eventData.nativeEvent.target.value === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id);
      if (targetIndex > 0) {
        if (data[targetIndex - 1].type !== 'sub-field') {
          onChange(removeChip(data[targetIndex - 1].id).resultData);
        } else {
          onChange(removeField(targetIndex));
        }
      }
    }
    // Remove next chip
    if (eventData.key === DELETE && eventData.nativeEvent.target.value === EMPTY_STRING) {
      const targetIndex = getIndexById(data, id) + 1;
      if (targetIndex < data.length && data[targetIndex].type === 'chip') {
        onChange(removeChip(data[targetIndex].id).resultData);
      }
    }
  };

  const selectItem = (id) => {
    onChange([...data.map(dataItem =>
      dataItem.type === 'chip' || dataItem.type === 'operator'
        ? dataItem.id === id ? { ...dataItem, selected: !dataItem.selected } : { ...dataItem, selected: false }
        : dataItem)
    ]);
  };

  const addItemToSelectedItems = (id) => {
    onChange([...data.map(dataItem =>
      dataItem.type === 'chip' || dataItem.type === 'operator'
        ? dataItem.id === id ? { ...dataItem, selected: !dataItem.selected } : dataItem
        : dataItem)
    ]);
  };

  const handleSingleChipClick = () => {
    clearTimeout(clickHandle.current);
    clickHandle.current = 0;
  };

  const handleDoubleChipClick = (id) => {
    clearTimeout(clickHandle.current);
    clickHandle.current = 0;
    const newData = convertChipToField(id);
    onChange(newData);
  };

  const handleChipClick = (eventData, id) => {
    if (eventData.shiftKey) {
      addItemToSelectedItems(id);
      return;
    }
    if (clickHandle.current) {
      handleDoubleChipClick(id);
      return;
    }
    selectItem(id);
    clickHandle.current = setTimeout(() => {
      handleSingleChipClick();
    }, 250);
  };

  const handleChipKeyDown = (eventData, id) => {
    // Focus on pre item
    if (eventData.key === ARROW_LEFT) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    if (eventData.key === BACKSPACE || eventData.key === DELETE) {
      eventData.nativeEvent.preventDefault();
      removeSelectedAndFocusedChips();
      return;
    }
    if (eventData.key === SPACE_KEY && eventData.shiftKey) {
      eventData.nativeEvent.preventDefault();
      addItemToSelectedItems(id);
      return;
    }
    if (eventData.key === ENTER_KEY) {
      const newData = convertChipToField(id);
      onChange(newData);
    }
    if (eventData.key === SPACE_KEY) {
      eventData.nativeEvent.preventDefault();
      selectItem(id);
    }
  };

  const handleOperatorKeyDown = (eventData, id) => {
    // Focus on pre item
    if (eventData.key === ARROW_LEFT) {
      const targetItemIndex = getIndexById(data, id) - 1;
      targetItemIndex > -1 && focusOnIndex(targetItemIndex, true);
    }

    // Focus on next item
    if (eventData.key === ARROW_RIGHT) {
      const targetItemIndex = getIndexById(data, id) + 1;
      targetItemIndex < data.length && focusOnIndex(targetItemIndex, true);
    }

    if (eventData.key === BACKSPACE || eventData.key === DELETE) {
      eventData.nativeEvent.preventDefault();
      removeSelectedAndFocusedChips();
      return;
    }
    if (eventData.key === SPACE_KEY && eventData.shiftKey) {
      eventData.nativeEvent.preventDefault();
      addItemToSelectedItems(id);
      return;
    }
    if (eventData.key === SPACE_KEY) {
      eventData.nativeEvent.preventDefault();
      selectItem(id);
    }
  };

  const handleOperatorClick = (eventData, id) => {
    if (eventData.shiftKey) {
      addItemToSelectedItems(id);
      return;
    }
    selectItem(id);
  };

  const handleAutoCompleteKeydown = (event, item) => {
    if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
      event.preventDefault();
      event.stopPropagation();
      submitAutoSuggestItem(item);
    }
  };

  const ariaString = () => {
    let aString = '';
    for (let ind = 0; ind < data.length; ind += 1) {
      if (data[ind].type === 'chip') {
        aString += `${data[ind].value} `;
      }
    }
    return aString;
  };

  const renderList = (listData) => {
    const {
      list,
      triggerEl,
    } = listData;
    if (!list) {
      return null;
    }
    const {
      event,
      items
    } = list;
    let virtualEl;
    // ContextualMenu should be rendered next to the pointer
    if (event && event.type === 'contextmenu') {
      const {
        pageX,
        pageY
      } = event;
      virtualEl = {
        current: {
          contains: () => false,
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            top: pageY,
            right: pageX,
            bottom: pageY,
            left: pageX,
          }),
        }
      };
    }
    return (
      <Menu {...{
        isOpen: true,
        triggerEl: virtualEl || triggerEl,
        ...CHIP_MENU_CONFIG
      }}
      >
        <List
          ref={searchWChipsListRef}
          style={{ maxHeight: '50vh' }}
        >
          {items.map(menuItem => (
            <ListItemBasic
              key={menuItem.key}
              {...{ ...menuItem, content: undefined }}
            >
              {typeof menuItem.content === 'string' ? (
                <ListItemText>
                  {menuItem.content}
                </ListItemText>
              ) : menuItem.content
              }
            </ListItemBasic>
          ))}
        </List>
      </Menu>
    );
  };

  const joinValues = (valueArray) => {
    let jointValue = '';
    valueArray.forEach((valueItem, index) => {
      jointValue += valueItem.value;
      jointValue = index === valueArray.length - 1 ? jointValue : jointValue += valueItem.separator;
    });
    return jointValue;
  };

  const handlePopupSubmit = (itemData) => {
    setPopupFocusID(null);
    if (itemData.type === 'sub-field') {
      onChange(convertSubFieldsToChip(itemData));
    } else {
      onChange(convertFieldToChip(itemData));
    }
  };

  const renderChip = (item, index) => {
    const {
      id,
      value,
      selected,
      onClick,
      onKeyDown,
      onBlur: onChipBlur,
      onFocus: onChipFocus,
      contextMenu,
      autocomplete,
      type,
      renderNode,
      ...otherChipProps
    } = item;
    const resolvedValue = typeof value === 'string' ? value : joinValues(value);
    const chipProps = {
      id,
      selected,
      key: id,
      onClick: (eventData) => {
        handleChipClick(eventData, id);
        onClick && onClick(eventData);
      },
      onKeyDown: (eventData) => {
        handleChipKeyDown(eventData, id);
        onKeyDown && onKeyDown(eventData);
      },
      onBlur: (eventData) => {
        item.focused && focusOnIndex(-1, true);
        onChipBlur && onChipBlur(eventData);
      },
      onFocus: (eventData) => {
        focusOnIndex(index, true);
        onChipFocus && onChipFocus(eventData);
      },
      'aria-label': selected ? `${resolvedValue} selected, press Enter to edit.` : `${resolvedValue}, press Space to select.`,
      ..._merge({}, CHIP_CONFIG, otherChipProps),
      ref: refs.current[index],
    };

    const handleRemoveClick = (event) => {
      event.stopPropagation();
      const newData = removeChip(item.id).resultData;
      onChange(newData);
    };

    let chip;
    if (renderNode) {
      chip = renderNode({
        ...chipProps,
        label: resolvedValue,
        onRemove: handleRemoveClick
      });
    } else {
      chip = (
        <Chip {...chipProps}>
          <ChipLabel label={resolvedValue} />
          <ChipIconButton onClick={handleRemoveClick}>
            <CloseIcon />
          </ChipIconButton>
        </Chip>
      );
    }

    return (
      <span key={item.id}>
        {chip}
        {renderList({ list: contextMenu, triggerEl: refs.current[index] })}
      </span>
    );
  };

  const renderOperator = (item, index) => {
    const {
      id,
      value,
      selected,
      onClick,
      onKeyDown,
      onBlur: onChipBlur,
      onFocus: onChipFocus,
      contextMenu,
      autocomplete,
      renderNode,
      ...otherOperatorProps
    } = item;
    const resolvedValue = typeof value === 'string' ? value : joinValues(value);
    const searchwChipsOperatorProps = {
      id,
      selected,
      size,
      key: id,
      onClick: (eventData) => {
        handleOperatorClick(eventData, id);
        onClick && onClick(eventData);
      },
      onKeyDown: (eventData) => {
        handleOperatorKeyDown(eventData, id);
        onKeyDown && onKeyDown(eventData);
      },
      onBlur: (eventData) => {
        item.focused === index && focusOnIndex(-1, true);
        onChipBlur && onChipBlur(eventData);
      },
      onFocus: (eventData) => {
        focusOnIndex(index, true);
        onChipFocus && onChipFocus(eventData);
      },
      'aria-label': selected ? `${resolvedValue} selected, press Enter to edit.` : `${resolvedValue}, press Space to select.`,
      theme: resolvedTheme.operator,
      ..._merge({}, { ...CHIP_CONFIG, style: {} }, otherOperatorProps),
      ref: refs.current[index],
      label: resolvedValue
    };

    let operator;
    if (renderNode) {
      operator = renderNode(searchwChipsOperatorProps);
    } else {
      operator = <SearchwChipsOperator {...searchwChipsOperatorProps} />;
    }

    return (
      <span key={item.id}>
        {operator}
        {renderList({ list: contextMenu, triggerEl: refs.current[index] })}
      </span>
    );
  };

  const renderInput = (item, index: number) => {
    const {
      id,
      contextMenu,
      autocomplete,
      onChange: fieldOnChange,
      onKeyDown,
      focused: inputFocused,
      inputProps,
      listProps,
      renderPopup,
      ...otherFieldProps
    } = item;
    const itemBefore: string = index > 0 ? data[index - 1].value : '';
    const itemAfter: string = index < data.length - 1 ? data[index + 1].value : '';
    let ariaLabel = index === 0 && data.length > 1 ? ARIA_LABEL_FIELD_AFTER + itemAfter : undefined;
    if (index > 0) {
      ariaLabel = index > 0 && index < data.length - 1 ?
        ARIA_LABEL_FIELD_BEFORE + itemBefore + ARIA_LABEL_FIELD_AFTER + itemAfter
        :
        ARIA_LABEL_FIELD_BEFORE + itemBefore;
    }
    const arrowUpHandler = autocomplete && autocomplete.items.find(autocompleteItem => !autocompleteItem.disabled);
    const autocompleteItems = autocomplete && autocomplete.items.map(autocompleteItem => ({
      ...autocompleteItem,
      onMouseDown: (event) => {
        event.preventDefault();
        event.stopPropagation();
        submitAutoSuggestItem({ ...autocompleteItem, id: item.id, index });
        autocompleteItem.onMouseDown && autocompleteItem.onMouseDown(event);
      },
      onKeyDown: (event) => {
        handleAutoCompleteKeydown(event, { ...autocompleteItem, id: item.id, index });
        if (event.key === ARROW_UP && arrowUpHandler && arrowUpHandler.key === autocompleteItem.key) {
          event.preventDefault();
          event.stopPropagation();
          focusOnIndex(index);
        }
        autocompleteItem.onKeyDown && autocompleteItem.onKeyDown(event);
      },
    }));
    const resolvedRenderPopup = renderPopup
      ? renderPopupProps => renderPopup({
        ref: searchWChipsPopupRef,
        onMouseDown: (event) => {
          event.preventDefault();
          event.stopPropagation();
          handlePopupFocus(item.id);
        },
        onBlur: handlePopupBlur,
        onSubmit: () => handlePopupSubmit({ ...item, index }),
        focusOnField: () => {
          handlePopupBlur();
          focusOnIndex(index, true, true);
        },
        ...renderPopupProps
      })
      : undefined;
    return (
      <SearchwChipsTextInput
        key={id}
        {...otherFieldProps}
        inputProps={{
          id,
          onFocus: () => { !inputFocused && focusOnIndex(index, true); },
          onBlur: () => { inputFocused && focusOnIndex(-1); },
          'aria-label': ariaLabel,
          tabIndex: data.length === index + 1 ? 0 : -1,
          ref: refs.current[index],
          ...inputProps
        }}
        size={size}
        lastInput={index === data.length - 1}
        onKeyDown={(eventData) => {
          handleFieldKeyDown(eventData, { id });
          onKeyDown && onKeyDown(eventData);
        }}
        onChange={(eventData) => {
          onChange(data.map(dataItem => dataItem.id === id ? { ...dataItem, value: (eventData.target as HTMLInputElement).value } : dataItem));
          fieldOnChange && fieldOnChange(eventData);
        }}
        listProps={{
          contextMenu,
          autocomplete: autocomplete ? { ...autocomplete, items: autocompleteItems } : undefined,
          triggerEl: refs.current[index],
          searchWChipsListRef,
          onMouseDown: () => { focusOnAttachment(id); },
          ...listProps
        }}
        focused={inputFocused}
        renderPopup={resolvedRenderPopup}
      />);
  };

  const renderSubField = (item, index:number) => {
    const {
      id,
      contextMenu,
      autocomplete,
      onChange: fieldOnChange,
      onKeyDown,
      focused: inputFocused,
      subField,
      inputProps,
      listProps,
      renderPopup,
      ...otherFieldProps
    } = item;
    const itemBefore: string = index > 0 ? data[index - 1].value : '';
    const itemAfter: string = index < data.length - 1 ? data[index + 1].value : '';
    let ariaLabel = index === 0 && data.length > 1 ? ARIA_LABEL_FIELD_AFTER + itemAfter : undefined;
    if (index > 0) {
      ariaLabel = index > 0 && index < data.length - 1 ?
        ARIA_LABEL_FIELD_BEFORE + itemBefore + ARIA_LABEL_FIELD_AFTER + itemAfter
        :
        ARIA_LABEL_FIELD_BEFORE + itemBefore;
    }
    const arrowUpHandler = autocomplete && autocomplete.items.find(autocompleteItem => !autocompleteItem.disabled);
    const autocompleteItems = autocomplete && autocomplete.items.map(autocompleteItem => ({
      ...autocompleteItem,
      onMouseDown: (event) => {
        event.preventDefault();
        event.stopPropagation();
        !autocompleteItem.submitChip && onChange(data.map(dataItem => dataItem.id === id ? { ...dataItem, focused: false, listFocused: true } : dataItem));
        autocompleteItem.onMouseDown && autocompleteItem.onMouseDown(event);
      },
      onMouseUp: (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleSubFieldAutocompleteItemClick({ ...autocompleteItem, id: item.id, index });
        autocompleteItem.onMouseUp && autocompleteItem.onMouseUp(event);
      },
      onKeyDown: (event) => {
        if (event.key === ENTER_KEY || event.key === SPACE_KEY) {
          event.preventDefault();
          event.stopPropagation();
          handleSubFieldAutocompleteItemClick({ ...autocompleteItem, id: item.id, index });
        }
        if (event.key === ARROW_UP && arrowUpHandler && arrowUpHandler.key === autocompleteItem.key) {
          event.preventDefault();
          event.stopPropagation();
          focusOnIndex(index);
        }
        autocompleteItem.onKeyDown && autocompleteItem.onKeyDown(event);
      },
      submitChip: undefined
    }));

    const {
      parentId: activeParentId
    } = data.find(dataItem =>
      dataItem.type === 'sub-field'
      && (dataItem.focused || dataItem.listFocused || dataItem.popupFocused))
      || {};

    const resolvedRenderPopup = renderPopup
      ? renderPopupProps => renderPopup({
        ref: searchWChipsPopupRef,
        onMouseDown: () => focusOnAttachment(item.id),
        onBlur: handlePopupBlur,
        onSubmit: () => handlePopupSubmit({ ...item, index }),
        focusOnField: () => {
          handlePopupBlur();
          focusOnIndex(index, true);
        },
        ...renderPopupProps
      })
      : undefined;

    return (
      <SearchwChipsTextInput
        subField={subField}
        key={id}
        {...otherFieldProps}
        inputProps={{
          id,
          onFocus: () => { focusOnIndex(index, true); },
          onBlur: () => { inputFocused && focusOnIndex(-1); },
          'aria-label': ariaLabel,
          tabIndex: data.length === index + 1 ? 0 : -1,
          ref: refs.current[index],
          ...inputProps
        }}
        size={size}
        lastInput={index === data.length - 1}
        onKeyDown={(eventData) => {
          handleSubFieldKeyDown(eventData, { id, index });
          onKeyDown && onKeyDown(eventData);
        }}
        onChange={(eventData) => {
          onChange(data.map(dataItem => dataItem.id === id ? { ...dataItem, value: (eventData.target as HTMLInputElement).value } : dataItem));
          fieldOnChange && fieldOnChange(eventData);
        }}
        listProps={{
          contextMenu,
          autocomplete: autocomplete ? { ...autocomplete, items: autocompleteItems } : undefined,
          triggerEl: refs.current[index],
          searchWChipsListRef,
          onMouseDown: () => { focusOnAttachment(id); },
          ...listProps
        }}
        focused={inputFocused}
        active={activeParentId === item.parentId}
        renderPopup={resolvedRenderPopup}
      />);
  };

  let isAnyItemFocused = false;
  refs.current = data.map((item) => {
    isAnyItemFocused = isAnyItemFocused || item.focused || item.listFocused || item.popupFocused;
    return createRef();
  });

  return (
    <SearchwChipsContext.Provider value={{ ...props }}>
      <SearchwChipsCore
        hovered={!!hovered}
        theme={resolvedTheme}
        focused={isAnyItemFocused}
        wrapperProps={{ 'aria-label': ariaString() }}
        {...otherProps}
        ref={ref}
        size={size}
        variant={variant}
      >
        {data.map((item, index) => {
          if (item.type === 'operator') {
            return renderOperator(item, index);
          }
          if (item.type === 'chip') {
            return renderChip(item, index);
          }
          if (item.type === 'sub-field') {
            return renderSubField(item, index);
          }
          return renderInput(item, index);
        })}
      </SearchwChipsCore>
    </SearchwChipsContext.Provider>
  );
}));

SearchwChips.displayName = 'SearchwChips';

SearchwChips.defaultProps = {
  onChange: () => {},
  size: 'medium',
  variant: 'underlined',
  autoFocus: false,
  error: false,
  SIcon: SearchwChipsIcon,
  beforeChips: undefined,
  afterChips: undefined,
  verticalDivider: false,
  theme: {},
  onFocus: undefined,
  onBlur: undefined
};

export {
  SearchwChips as default,
  SearchwChipsCore,
  SearchwChipsContext,
  SearchwChipsTextInput
};
